import React, { useEffect, useState } from "react";
import {
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  MenuItem,
  Container,
  Box,
} from "@mui/material";
import { concat, find, map, pullAt } from "lodash";
import InteiroInput from "../../../util/ui/inputs/inteiroInput";
import MoedaInput from "../../../util/ui/inputs/moedaInput";
import InteiroText from "../../../util/ui/inputs/inteiroText";
import MoedaText from "../../../util/ui/inputs/moedaText";
import { Moment } from "moment";
import Secao from "../../../util/ui/secao";
import { IconTrashX, IconPlus, IconArrowDownCircle } from "@tabler/icons-react";
import PosicaoAtivo from "../../../../models/posicaoAtivo";
import { useUsuario } from "../../../../providers/usuarioProvider";
import Posicao from "../../../../models/posicao";
import Ativo from "../../../../models/ativo";
import {
  Mercado,
  Negociacao,
  TipoOperacao,
  ClasseAtivo,
} from "../../../../models/enum";
import Functions from "../../../../callable/functions";
import Ticker from "../../../util/ui/ticker";
import { onSnapshot } from "firebase/firestore";
import PosicaoDb from "../../../../db/posicaoDb";
import { LoadingButton } from "@mui/lab";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useSnackBar } from "../../../../stores/snackBarStores";
import { felixTokens } from "../../../../theme";

/** Ticker e data são dado, não texto: mono tabular dentro do campo. */
const campoDado = {
  "& input": {
    fontFamily: felixTokens.fontMono,
    fontVariantNumeric: "tabular-nums",
  },
};

type Props = {
  data: Moment;
};

export default function TabelaCustodia({ data }: Props) {
  const { visualizado } = useUsuario();
  const [posicao, setPosicao] = useState<Posicao>(
    new Posicao({
      data: data,
      posicoes: [],
    })
  );
  const [ativo, setAtivo] = useState<Ativo>();
  const [mercado, setMercado] = useState<Mercado>(Mercado.A_VISTA);
  const [tipoOperacao, setTipoOperacao] = useState<TipoOperacao>(
    TipoOperacao.COMPRA
  );
  const [codigoAtivo, setCodigoAtivo] = useState<string>("");
  const [prazo, setPrazo] = useState<Moment | undefined>();
  const [quantidade, setQuantidade] = useState<number>(0);
  const [precoMedio, setPrecoMedio] = useState<number>(0);
  const [valorTotal, setValorTotal] = useState<number>(0);
  const [salvando, setSalvando] = useState(false);
  const snackbar = useSnackBar();

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        new PosicaoDb().doc({
          usuarioId: visualizado,
          posicaoId: data.format("YYYY-MM-DD"),
        }),
        (snapshot) => {
          if (snapshot.exists()) setPosicao(snapshot.data()!);
        }
      );
  }, [data, visualizado]);

  const updatePreco = (quantidade: number = 0, precoMedio: number = 0) => {
    setQuantidade(quantidade);
    setPrecoMedio(precoMedio);
    setValorTotal(quantidade * precoMedio);
  };

  const onChangeCodigoAtivo = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setCodigoAtivo(e.target.value.toUpperCase());
    if (e.target.value.toUpperCase().length >= 5)
      return new Functions()
        .ativoAutoComplete({
          codigoAtivo: e.target.value.toUpperCase(),
        })
        .then((res) => {
          setAtivo(res.data?.ativo);
        });
  };

  async function salvar() {
    setSalvando(true);
    await new Functions()
      .posicaoInicial({ posicaoInicial: posicao.toJSON() })
      .finally(() => setSalvando(false));
  }

  function adicionarAtivo() {
    if (!!visualizado) {
      if (!!!find(posicao.posicoes, (p) => p.codigoAtivo === codigoAtivo)) {
        let novaPosicao = posicao.posicoes;
        novaPosicao = concat(
          posicao.posicoes,
          new PosicaoAtivo({
            codigoAtivo: codigoAtivo,
            classeAtivo:
              mercado === Mercado.OPCOES
                ? ClasseAtivo.OPCAO
                : ativo!.classeAtivo,
            mercado: mercado,
            precoMedio: precoMedio,
            quantidade: quantidade,
            tipoNegociacao: Negociacao.BOVESPA,
            tipoOperacao: tipoOperacao,
            valorTotal: valorTotal,
            prazo: prazo,
          })
        );
        setPosicao((prev) => new Posicao({ ...prev, posicoes: novaPosicao }));

        setMercado(Mercado.A_VISTA);
        setTipoOperacao(TipoOperacao.COMPRA);
        setCodigoAtivo("");
        setPrazo(undefined);
        setQuantidade(0);
        setPrecoMedio(0);
        setValorTotal(0);
        setAtivo(undefined);
      } else {
        snackbar({
          type: "ALERT",
          payload: {
            severidade: "warning",
            mensagem: `Você já adicionou o ativo ${codigoAtivo}. Você deve adicionar uma linha para cada ativo com o preço médio e quantidades que existiam na sua carteira em ${data.format(
              "DD/MM/YYYY"
            )}.`,
          },
        });
      }
    }
  }

  const apagarAtivo = (index: number) => {
    if (!!visualizado) {
      let novaPosicao = posicao.posicoes;
      pullAt(novaPosicao, [index]);
      setPosicao((prev) => new Posicao({ ...prev, posicoes: novaPosicao }));
    }
  };

  return (
    <Container fixed disableGutters sx={{ py: 1 }}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        paddingBottom={2}
      >
        <Typography variant="h3" color="text.primary">
          Custódia em{" "}
          <Typography
            component="span"
            variant="dataMono"
            sx={{ fontSize: "inherit", fontWeight: 500 }}
          >
            {data.format("DD/MM/YYYY")}
          </Typography>
        </Typography>
        <Box paddingX={1} />
        <LoadingButton
          variant="contained"
          size="small"
          color="success"
          onClick={salvar}
          loadingPosition="start"
          startIcon={<IconArrowDownCircle />}
          loading={salvando}
        >
          Salvar
        </LoadingButton>
      </Box>
      <Secao titulo="Custódia" padding={0}>
        <TableContainer>
          <Table
            size="small"
            padding="none"
            sx={{
              "& .MuiTableCell-paddingNone": {
                px: 1,
                "&:first-of-type": { pl: 2 },
                "&:last-of-type": { pr: 2 },
              },
            }}
          >
            {/* Header do sistema: mono uppercase tracked em ink-60 sobre
                surface — o tema já resolve, nada de barra sólida de tinta. */}
            <TableHead>
              <TableRow>
                <TableCell>Mercado</TableCell>
                <TableCell>C/V</TableCell>
                <TableCell>Código do Ativo</TableCell>
                <TableCell>Classe</TableCell>
                <TableCell>Prazo</TableCell>
                <TableCell align="right">Qtd</TableCell>
                <TableCell align="right">Preço Médio</TableCell>
                <TableCell align="right">Valor Total</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {map(posicao.posicoes, (ativo, index) => (
                <TableRow key={index} sx={{ height: 40 }}>
                  <TableCell>
                    <Typography variant="body2">{ativo.mercado}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="labelMono" color="text.secondary">
                      {ativo.tipoOperacao}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Ticker codigoAtivo={ativo.codigoAtivo} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="labelMono" color="text.secondary">
                      {ativo.classeAtivo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="dataMono" color="text.secondary">
                      {ativo.prazo?.format("DD/MM/YYYY") ?? ""}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="dataMono">
                      <InteiroText
                        value={ativo.quantidade}
                        displayType={"text"}
                      ></InteiroText>
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="dataMono">
                      <MoedaText
                        decimalScale={4}
                        displayType={"text"}
                        value={ativo.precoMedio}
                      ></MoedaText>
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="dataMono" sx={{ fontWeight: 500 }}>
                      <MoedaText
                        displayType={"text"}
                        value={ativo.valorTotal}
                      ></MoedaText>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="delete"
                      color="error"
                      onClick={() => {
                        apagarAtivo(index);
                      }}
                      size="small"
                    >
                      <IconTrashX size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ height: 80 }}>
                <TableCell>
                  <TextField
                    id="standard-mercado"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    sx={{ width: "13ch" }}
                    select
                    value={mercado ?? ""}
                    onChange={(e) => setMercado(e.target.value as Mercado)}
                  >
                    <MenuItem key={Mercado.A_VISTA} value={Mercado.A_VISTA}>
                      Vista
                    </MenuItem>
                    <MenuItem key={Mercado.OPCOES} value={Mercado.OPCOES}>
                      Opções
                    </MenuItem>
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    id="standard-tipoOperacao"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    sx={{ width: "6ch" }}
                    select
                    value={tipoOperacao ?? ""}
                    onChange={(e) =>
                      setTipoOperacao(e.target.value as TipoOperacao)
                    }
                  >
                    <MenuItem
                      key={TipoOperacao.COMPRA}
                      value={TipoOperacao.COMPRA}
                    >
                      C
                    </MenuItem>
                    <MenuItem
                      key={TipoOperacao.VENDA}
                      value={TipoOperacao.VENDA}
                    >
                      V
                    </MenuItem>
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    id="standard-codigoAtivo"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{ width: "14ch", ...campoDado }}
                    variant="outlined"
                    value={codigoAtivo ?? ""}
                    onChange={onChangeCodigoAtivo}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    id="standard-classeAtivo"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{ width: "12ch" }}
                    variant="outlined"
                    disabled
                    value={
                      mercado === Mercado.OPCOES
                        ? ClasseAtivo.OPCAO
                        : ativo?.classeAtivo ?? ""
                    }
                  />
                </TableCell>
                <TableCell>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      sx={{ width: "18ch", ...campoDado }}
                      slotProps={{ textField: { size: "small" } }}
                      disabled={mercado !== "Opções"}
                      value={prazo ?? null}
                      onChange={(e) => {
                        if (e?.isValid()) setPrazo(e.startOf("day"));
                      }}
                    />
                  </LocalizationProvider>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    id="standard-quantidade"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{ width: "7ch" }}
                    variant="outlined"
                    value={quantidade}
                    onChange={(e) =>
                      updatePreco((e.target as any).floatValue, precoMedio)
                    }
                    InputProps={{
                      inputComponent: InteiroInput as any,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    id="standard-precoMedio"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{ width: "15ch" }}
                    variant="outlined"
                    value={precoMedio}
                    onChange={(e) =>
                      updatePreco(quantidade, (e.target as any).floatValue)
                    }
                    InputProps={{
                      inputComponent: MoedaInput as any,
                      inputProps: { decimalScale: 4 },
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    id="standard-valorTotal"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{ width: "16ch", "& input": { fontWeight: 500 } }}
                    variant="outlined"
                    disabled
                    value={valorTotal}
                    InputProps={{
                      inputComponent: MoedaInput as any,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-label="save"
                    disabled={
                      !(
                        codigoAtivo &&
                        valorTotal &&
                        valorTotal > 0 &&
                        (mercado === Mercado.OPCOES || !!ativo)
                      )
                    }
                    color="success"
                    onClick={adicionarAtivo}
                    size="small"
                  >
                    <IconPlus size={18} />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Secao>
    </Container>
  );
}
